/**
 * Dashboard Constants, Types & Helpers
 * Extracted from DashboardPage.tsx
 */

import { projectId } from "../../../../utils/supabase/info";

/* ================================================================
   Constants
   ================================================================ */

/** LocalStorage key for the cached share domain from system settings */
const SHARE_DOMAIN_CACHE_KEY = "zap_share_domain";

/**
 * Async: fetch shareDomain from the server settings API, cache it,
 * and return it. Used by share modals to always get the freshest value.
 * Falls back to localStorage cache / origin if the fetch fails.
 */
export async function loadFreshShareDomain(): Promise<string> {
  try {
    // Dynamic import to avoid circular deps
    const { getSettings } = await import("../../utils/settingsApi");
    const s = await getSettings();
    console.log("[ShareDomain] Loaded from API:", s.shareDomain || "(empty)");
    cacheShareDomain(s.shareDomain);
    if (s.shareDomain) return s.shareDomain.replace(/\/+$/, "");
  } catch (err) {
    console.warn("[ShareDomain] API fetch failed, using cache:", err);
  }
  // Fallback to cache / origin
  return getPublishedBaseUrl();
}

/**
 * Cache the shareDomain from system settings into localStorage
 * so that synchronous URL helpers can read it without an async call.
 * Called by any component that loads settings (e.g. DashboardPage, SystemSettingsPage).
 */
export function cacheShareDomain(domain: string | undefined | null): void {
  try {
    if (domain) {
      const clean = domain.replace(/\/+$/, "");
      localStorage.setItem(SHARE_DOMAIN_CACHE_KEY, clean);
      console.log("[ShareDomain] Cached:", clean);
    } else {
      localStorage.removeItem(SHARE_DOMAIN_CACHE_KEY);
      console.log("[ShareDomain] Cleared cache (domain was empty)");
    }
  } catch { /* ignore */ }
}

/**
 * Base URL for share links.
 *
 * Priority:
 *   1. Explicit override (passed by caller)
 *   2. Cached shareDomain from system settings (localStorage)
 *   3. window.location.origin (current browser URL)
 *   4. Hardcoded fallback
 */
export function getPublishedBaseUrl(overrideDomain?: string): string {
  // 1. Explicit caller override
  if (overrideDomain) return overrideDomain.replace(/\/+$/, "");

  // 2. Cached settings domain
  try {
    const cached = localStorage.getItem(SHARE_DOMAIN_CACHE_KEY);
    if (cached) return cached;
  } catch { /* ignore */ }

  // 3. Current origin
  if (typeof window !== "undefined") return window.location.origin;

  // 4. Fallback
  return "https://zap-morin.online";
}

/** @deprecated Use getPublishedBaseUrl() instead */
export const PUBLISHED_BASE_URL = "https://zap-morin.online";

/**
 * Generate the published share URL (always points to the deployed site).
 *
 * Uses query-param format (?t=ID) instead of hash (#/t/ID/) so that
 * WhatsApp, Telegram, email clients, and other apps don't truncate the URL
 * at the # character. App.tsx intercepts ?t= on load and redirects to the
 * hash route.
 */
export function getPublishedShareUrl(templateId: string, overrideDomain?: string): string {
  return `${getPublishedBaseUrl(overrideDomain)}/?t=${encodeURIComponent(templateId)}`;
}

/**
 * Generate the client board share URL (always points to the deployed site).
 *
 * Uses query-param format (?cb=ID) instead of hash (#/client-board/ID) so
 * that WhatsApp, Telegram, email clients, and other apps don't truncate the
 * URL at the # character. App.tsx intercepts ?cb= on load and redirects to
 * the hash route.
 */
export function getClientBoardShareUrl(templateId: string, overrideDomain?: string): string {
  return `${getPublishedBaseUrl(overrideDomain)}/?cb=${encodeURIComponent(templateId)}`;
}

/* ================================================================
   Open Graph Share URLs — Rich previews for social media
   ================================================================
   These URLs point to the server's /og endpoint which returns HTML
   with proper og:title, og:description, og:image meta tags.
   Social media crawlers read these tags to generate rich preview cards.
   Users are automatically redirected to the actual app.
   ================================================================ */

/** Server API base URL (for OG endpoints) */
function getOgBaseUrl(): string {
  if (projectId) {
    return `https://${projectId}.supabase.co/functions/v1/make-server-6a230365`;
  }
  return "";
}

/**
 * Get the OG-enriched share URL for a template.
 * Falls back to the standard share URL if OG base is unavailable.
 */
export function getOgShareUrl(templateId: string): string {
  const ogBase = getOgBaseUrl();
  if (ogBase) return `${ogBase}/og?t=${encodeURIComponent(templateId)}`;
  return getPublishedShareUrl(templateId);
}

/**
 * Get the OG-enriched share URL for a client intake form.
 */
export function getOgIntakeShareUrl(intakeToken: string): string {
  const ogBase = getOgBaseUrl();
  if (ogBase) return `${ogBase}/og?intake=${encodeURIComponent(intakeToken)}`;
  return `${getPublishedBaseUrl()}/?page=intake&id=${encodeURIComponent(intakeToken)}`;
}

/**
 * Get the OG-enriched share URL for a client board.
 */
export function getOgClientBoardShareUrl(templateId: string): string {
  const ogBase = getOgBaseUrl();
  if (ogBase) return `${ogBase}/og?cb=${encodeURIComponent(templateId)}`;
  return getClientBoardShareUrl(templateId);
}

/**
 * Get the OG-enriched share URL for a named page (intake-forms, client-portal, etc).
 */
export function getOgPageShareUrl(pageName: string, id?: string): string {
  const ogBase = getOgBaseUrl();
  const idParam = id ? `&id=${encodeURIComponent(id)}` : "";
  if (ogBase) return `${ogBase}/og?page=${encodeURIComponent(pageName)}${idParam}`;
  return `${getPublishedBaseUrl()}/?page=${encodeURIComponent(pageName)}${idParam}`;
}

/* ================================================================
   Color Helpers
   ================================================================ */

export const ACCENT_COLORS = [
  { bg: "from-zap to-zap-light", light: "bg-zap-pale", text: "text-zap", border: "border-zap-light/30" },
  { bg: "from-zap-navy to-zap", light: "bg-zap-pale", text: "text-zap-navy", border: "border-zap/20" },
  { bg: "from-zap-dark to-zap-light", light: "bg-zap-pale", text: "text-zap-dark", border: "border-zap-light/30" },
  { bg: "from-zap-light to-zap-navy", light: "bg-zap-pale", text: "text-zap", border: "border-zap/20" },
  { bg: "from-zap to-zap-dark", light: "bg-zap-pale", text: "text-zap-dark", border: "border-zap-dark/20" },
  { bg: "from-zap-navy to-zap-light", light: "bg-zap-pale", text: "text-zap-navy", border: "border-zap-light/30" },
  { bg: "from-zap-dark to-zap", light: "bg-zap-pale", text: "text-zap", border: "border-zap/20" },
  { bg: "from-zap to-zap-navy", light: "bg-zap-pale", text: "text-zap-navy", border: "border-zap-dark/20" },
];

export type AccentColor = typeof ACCENT_COLORS[0];

export function getAccent(id: string): AccentColor {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return ACCENT_COLORS[Math.abs(hash) % ACCENT_COLORS.length];
}

export function getInitials(name: string): string {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2);
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Tag colors as inline hex values to avoid Tailwind v4 dynamic-class scanning issues */
export const TAG_COLORS: { bg: string; text: string; border: string; dot: string }[] = [
  { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", dot: "#0ea5e9" },
  { bg: "#ffe4e6", text: "#be123c", border: "#fecdd3", dot: "#f43f5e" },
  { bg: "#ede9fe", text: "#6d28d9", border: "#ddd6fe", dot: "#8b5cf6" },
  { bg: "#ccfbf1", text: "#0f766e", border: "#99f6e4", dot: "#14b8a6" },
  { bg: "#ffedd5", text: "#c2410c", border: "#fed7aa", dot: "#f97316" },
  { bg: "#fce7f3", text: "#be185d", border: "#fbcfe8", dot: "#ec4899" },
  { bg: "#e0e7ff", text: "#4338ca", border: "#c7d2fe", dot: "#6366f1" },
  { bg: "#ecfccb", text: "#4d7c0f", border: "#d9f99d", dot: "#84cc16" },
  { bg: "#fae8ff", text: "#a21caf", border: "#f5d0fe", dot: "#d946ef" },
  { bg: "#cffafe", text: "#0e7490", border: "#a5f3fc", dot: "#06b6d4" },
];

export function getTagColor(tag: string) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 37 + tag.charCodeAt(i)) | 0;
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

export const ROLE_LABELS: Record<string, string> = {
  admin: "\u05DE\u05E0\u05D4\u05DC",
  characterizer: "\u05DE\u05D0\u05E4\u05D9\u05D9\u05E0\u05EA",
  designer: "\u05DE\u05E2\u05E6\u05D1\u05EA",
  team_lead: "\u05DE\u05EA\u05F4\u05DC",
  content_writer: "\u05DB\u05D5\u05EA\u05D1\u05EA \u05EA\u05D5\u05DB\u05DF",
  developer: "\u05DE\u05E4\u05EA\u05D7",
  marketer: "\u05DE\u05E7\u05D3\u05DE\u05EA",
  client_manager: "\u05DE\u05E0\u05D4\u05DC\u05EA \u05DC\u05E7\u05D5\u05D7",
  onboarding: "\u05D0\u05D5\u05E0\u05D1\u05D5\u05E8\u05D3\u05D9\u05E0\u05D2",
};

export const ROLE_BADGE_COLORS: Record<string, string> = {
  admin: "bg-amber-100 text-amber-700 border-amber-200",
  characterizer: "bg-violet-100 text-violet-700 border-violet-200",
  designer: "bg-sky-100 text-sky-700 border-sky-200",
  team_lead: "bg-teal-100 text-teal-700 border-teal-200",
  content_writer: "bg-emerald-100 text-emerald-700 border-emerald-200",
  developer: "bg-orange-100 text-orange-700 border-orange-200",
  marketer: "bg-pink-100 text-pink-700 border-pink-200",
  client_manager: "bg-rose-100 text-rose-700 border-rose-200",
  onboarding: "bg-cyan-100 text-cyan-700 border-cyan-200",
};

/** Role icon names (for reference in components) */
export const ROLE_ICONS: Record<string, string> = {
  admin: "Shield",
  characterizer: "FileText",
  designer: "Palette",
  team_lead: "UserGroup",
  content_writer: "PenTool",
  developer: "Code",
  marketer: "Megaphone",
  client_manager: "UserCheck",
  onboarding: "UserPlus",
};

/* ================================================================
   Types
   ================================================================ */

export type DashboardTab = "templates" | "masters" | "users" | "archive";
export type ViewMode = "grid" | "list";
export type FilterType = "all" | "branding" | "store" | "facelift";
export type SortKey = "updatedAt-desc" | "createdAt-desc" | "clientName-asc" | "clientName-desc";

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "updatedAt-desc", label: "\u05E2\u05D3\u05DB\u05D5\u05DF \u05D0\u05D7\u05E8\u05D5\u05DF" },
  { value: "createdAt-desc", label: "\u05E0\u05D5\u05E6\u05E8 \u05D0\u05D7\u05E8\u05D5\u05DF" },
  { value: "clientName-asc", label: "\u05E9\u05DD \u05DC\u05E7\u05D5\u05D7 \u05D0-\u05EA" },
  { value: "clientName-desc", label: "\u05E9\u05DD \u05DC\u05E7\u05D5\u05D7 \u05EA-\u05D0" },
];

/* ================================================================
   Helpers
   ================================================================ */

export function formatShortDate(ts: number): string {
  if (!ts) return "\u2014";
  const d = new Date(ts);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
}

/** Format timestamp as relative time, e.g. "\u05DC\u05E4\u05E0\u05D9 2 \u05E9\u05E2\u05D5\u05EA" */
export function formatRelativeTime(ts: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "\u05D4\u05E8\u05D2\u05E2";
  if (minutes < 60) return `\u05DC\u05E4\u05E0\u05D9 ${minutes} \u05D3\u05E7\u05F3`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `\u05DC\u05E4\u05E0\u05D9 ${hours} \u05E9\u05E2\u05D5\u05EA`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `\u05DC\u05E4\u05E0\u05D9 ${days} \u05D9\u05DE\u05D9\u05DD`;
  return formatShortDate(ts);
}

/* ================================================================
   Template Workflow Status
   ================================================================ */

export const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; dot: string }> = {
  onboarding:            { label: "\u05D0\u05D5\u05E0\u05D1\u05D5\u05E8\u05D3\u05D9\u05E0\u05D2",     bg: "#fef3c7", text: "#92400e", border: "#fbbf24", dot: "#f59e0b" },
  characterization_call: { label: "\u05E9\u05D9\u05D7\u05EA \u05D0\u05E4\u05D9\u05D5\u05DF",          bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd", dot: "#8b5cf6" },
  materials:             { label: "\u05D7\u05D5\u05DE\u05E8\u05D9\u05DD",              bg: "#e0f2fe", text: "#075985", border: "#7dd3fc", dot: "#0ea5e9" },
  characterization:      { label: "\u05D0\u05E4\u05D9\u05D5\u05DF",              bg: "#f3e8ff", text: "#7e22ce", border: "#d8b4fe", dot: "#a855f7" },
  seo_check:             { label: "\u05D1\u05D3\u05D9\u05E7\u05EA SEO",          bg: "#dcfce7", text: "#166534", border: "#86efac", dot: "#22c55e" },
  client_approval:       { label: "\u05D0\u05D9\u05E9\u05D5\u05E8 \u05DC\u05E7\u05D5\u05D7",         bg: "#dbeafe", text: "#1e40af", border: "#93c5fd", dot: "#3b82f6" },
  design:                { label: "\u05E2\u05D9\u05E6\u05D5\u05D1",              bg: "#fce7f3", text: "#9d174d", border: "#f9a8d4", dot: "#ec4899" },
  development:           { label: "\u05E4\u05D9\u05EA\u05D5\u05D7",              bg: "#cffafe", text: "#155e75", border: "#67e8f9", dot: "#06b6d4" },
  site_tree:             { label: "\u05E2\u05E5 \u05D0\u05EA\u05E8",             bg: "#d1fae5", text: "#065f46", border: "#6ee7b7", dot: "#10b981" },
  matal_handling:        { label: "\u05D1\u05D8\u05D9\u05E4\u05D5\u05DC \u05DE\u05EA\u05DC",         bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", dot: "#ef4444" },
  none:                  { label: "\u05D0\u05D9\u05DF",                bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db", dot: "#9ca3af" },
  monday_add:            { label: "\u05D4\u05D5\u05E1\u05E4\u05D4 \u05DC\u05DE\u05D0\u05E0\u05D3\u05D9\u05D9",       bg: "#fff7ed", text: "#9a3412", border: "#fdba74", dot: "#f97316" },
  logo:                  { label: "\u05DC\u05D5\u05D2\u05D5",               bg: "#fdf4ff", text: "#86198f", border: "#f0abfc", dot: "#d946ef" },
  content_writing:       { label: "\u05DB\u05EA\u05D9\u05D1\u05EA \u05EA\u05D5\u05DB\u05DF",         bg: "#ecfccb", text: "#3f6212", border: "#bef264", dot: "#84cc16" },
  integrations:          { label: "\u05D7\u05D9\u05D1\u05D5\u05E8 \u05DE\u05DE\u05E9\u05E7\u05D9\u05DD",        bg: "#e0e7ff", text: "#3730a3", border: "#a5b4fc", dot: "#6366f1" },
  pm_handling:           { label: "\u05D1\u05D8\u05D9\u05E4\u05D5\u05DC \u05DE\u05E0\u05D4\u05DC \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",  bg: "#f1f5f9", text: "#334155", border: "#94a3b8", dot: "#64748b" },
};

/* ================================================================
   Completion meter color helper (reused in dashboard)
   ================================================================ */

export function getCompletionColor(pct: number): string {
  if (pct >= 80) return "#22c55e"; // green-500
  if (pct >= 50) return "#eab308"; // yellow-500
  if (pct >= 25) return "#f97316"; // orange-500
  return "#ef4444"; // red-500
}

/* ================================================================
   Project Type Configuration (SEO / WP / WIX)
   ================================================================ */

export type ProjectTypeKey = "seo" | "wp" | "wix";

export const PROJECT_TYPE_CONFIG: Record<ProjectTypeKey, { label: string; bg: string; text: string; border: string }> = {
  seo: { label: "SEO", bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" },
  wp:  { label: "WP",  bg: "#e0e7ff", text: "#4338ca", border: "#a5b4fc" },
  wix: { label: "WIX", bg: "#fce7f3", text: "#be185d", border: "#fbcfe8" },
};